import React from 'react';
import { Box, Paper, Typography } from '@mui/material';
import RelativeTile from './RelativeTile';
import { ADD_RELATIVE_KEYS } from '../../../pedigree-tool/keyboardShortcuts';
import ReproLauncher from './ReproLauncher';
import SpouseSelector from './SpouseSelector';
import { PersonSymbol, ReproSymbol } from './TileSymbol';
import { useTileColours } from './tileTheme';
import { GROUP_GAP } from './tileMetrics';
import { useAutoTranslation } from 'src/hooks/useTranslate';

/**
 * The tinted container that makes the child scope visible at rest.
 *
 * THIS IS THE POINT OF THE REDESIGN. Son, Daughter and Add fetus sit visibly
 * INSIDE a bordered box headed by the spouse selector, so nothing needs help text
 * to explain what the selector governs. Changing the selector changes what those
 * three controls do and nothing else in the panel — in the old flat stack, a
 * "Child with (partner)" dropdown sat mid-list with no indication of its reach.
 *
 * Owns nothing: every value and handler comes from the container.
 *
 * NO ADD-SPOUSE CONTROL LIVES HERE. There is exactly one route to adding a
 * spouse, the tile in Current generation. An earlier draft put a "+" in this
 * header and a reviewer went looking for how to add a spouse and could not find
 * it — and it is the wrong place semantically for a married couple with no
 * children.
 *
 * @param {Object} props
 * @param {Array}  props.spouseOptions   From spouseOptionsFor, each with `pid`.
 * @param {?number} props.selectedSpousePid
 * @param {Function} props.onSelectSpouse
 * @param {Function} props.onAddChild     (gender, count) => void
 * @param {boolean} props.disabled
 * @param {Object}  props.allow           { son, daughter } from allowedActions.
 * @param {?Object} props.fetus           null hides the launcher entirely (D7).
 *                                        { disabled, reason, onOpen } otherwise.
 * @param {?string} props.scopeSpouse     The selected spouse's label, for the
 *                                        accessible names ("Add son with Jane
 *                                        Doe"); null when there is none.
 */
const ChildScope = ({
  spouseOptions,
  selectedSpousePid,
  onSelectSpouse,
  onAddChild,
  disabled,
  allow,
  fetus,
  scopeSpouse
}) => {
  const t = useAutoTranslation();

  const c = useTileColours();
  return (
    <Box
      component="fieldset"
      sx={{ m: 0, p: 0, border: 0, minWidth: 0, mt: GROUP_GAP }}
    >
      <Typography
        component="legend"
        sx={{
          p: 0,
          mb: 0.75,
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: 'text.secondary'
        }}
      >
        {t('Children')}
      </Typography>
      <Paper
        variant="outlined"
        sx={{
          p: 1.25,
          borderRadius: 1.5,
          // A whisper of the accent rather than a grey fill, so the box reads as the
          // scope the selector governs instead of as dead space behind the tiles.
          bgcolor: c.scopeTint,
          borderColor: c.scopeBorder
        }}
      >
        <SpouseSelector
          options={spouseOptions}
          value={selectedSpousePid}
          onChange={onSelectSpouse}
          disabled={disabled}
        />
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
            gap: 1
          }}
        >
          {allow.son && (
            <RelativeTile
              vaAnchor="pedigree.add-relative.son"
              symbol={<PersonSymbol gender="male" />}
              label={t('Son')}
              accessibleName={
                scopeSpouse
                  ? t('Add son with {{spouse}}', { spouse: scopeSpouse })
                  : t('Add son')
              }
              shortcut={ADD_RELATIVE_KEYS.son}
              disabled={disabled}
              onAdd={(count) => onAddChild('male', count)}
            />
          )}
          {allow.daughter && (
            <RelativeTile
              vaAnchor="pedigree.add-relative.daughter"
              symbol={<PersonSymbol gender="female" />}
              label={t('Daughter')}
              accessibleName={
                scopeSpouse
                  ? t('Add daughter with {{spouse}}', { spouse: scopeSpouse })
                  : t('Add daughter')
              }
              shortcut={ADD_RELATIVE_KEYS.daughter}
              disabled={disabled}
              onAdd={(count) => onAddChild('female', count)}
            />
          )}
        </Box>
        {/* A fetus is a child not yet born, so it belongs to the same scope as the
          tiles above rather than to a group of its own — and it inherits the
          selected spouse as "conceived with" (TGCLOUD-1617 item 3). */}
        {fetus && (
          <Box sx={{ mt: 1.25 }}>
            <ReproLauncher
              symbol={<ReproSymbol letter="P" />}
              label={t('Add fetus…')}
              accessibleName={
                scopeSpouse
                  ? t('Add fetus with {{spouse}}, opens the pregnancy screen', {
                      spouse: scopeSpouse
                    })
                  : t('Add fetus, opens the pregnancy screen')
              }
              onOpen={fetus.onOpen}
              disabled={disabled || fetus.disabled}
              disabledReason={fetus.disabled ? fetus.reason : ''}
            />
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default ChildScope;
